import React, { memo, useMemo } from 'react'
import dayjs from 'dayjs'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableRowData,
  Highlight,
  useSidePanel,
} from '@node-space/ui-components'
import { TransactionContent } from 'types/transaction'
import { TransactionType } from 'types/transaction/TransactionType'
import { paymentDirections } from 'types/types'
import { getHighlightType, titleCase } from 'utils/utils'
import { transformSearchResultsByPaymentType } from './searchWalletTransformer'
import { SidePanelSummary } from './SidePanel/SidePanelSummary'
import { TransactionTypeAggregation } from './types'

interface SearchTransactionTableProps {
  walletTransactions: {
    transactions: { content: TransactionContent[] }
    aggregations: TransactionTypeAggregation[]
  }
}

const SearchTransactionTable = ({ walletTransactions }: SearchTransactionTableProps) => {
  const { t } = useTranslation()
  const {
    isSidePanelOpen: isTransactionDetailsOpen,
    sidePanelData: transactionDetails,
    paging: pageTransactions,
    setIsSidePanelOpen: setIsTransactionDetailsOpen,
    updateSidePanel: updateTransactionDetails,
  } = useSidePanel(walletTransactions?.transactions?.content)

  const openSidePanelSummary = (index: number) => {
    updateTransactionDetails(index)
    setIsTransactionDetailsOpen(true)
  }
  const columns = useMemo(() => {
    return [
      { key: 'date', label: t('date'), variant: DataTableCellVariants.TEXT },
      { key: 'transactionType', label: t('transactionType'), variant: DataTableCellVariants.TEXT },
      { key: 'from', label: t('from'), variant: DataTableCellVariants.TEXT },
      { key: 'to', label: t('to'), variant: DataTableCellVariants.TEXT },
      {
        key: 'amount',
        label: t('amount'),
        variant: DataTableCellVariants.TEXT,
        align: DataTableColumnAlignments.RIGHT,
      },
      { key: 'status', label: t('status'), variant: DataTableCellVariants.HIGHLIGHT },
    ]
  }, [])

  const tableData = useMemo((): DataTableRowData[] => {
    return walletTransactions?.transactions?.content?.map((transaction, transactionIndex) => {
      const isPaymentIn = transaction?.direction === paymentDirections.IN && transaction?.amount > 0
      const transformedData = transformSearchResultsByPaymentType(transaction)
      const latePayment = transaction?.type === TransactionType.LATE_PAYMENT
      const transactionRowId = `${transaction?.transactionUUID}-${transaction?.hash}-${transaction?.dateCreated}`

      return {
        id: transactionRowId,
        testid: transactionRowId,
        onRowClick: () => {
          openSidePanelSummary(transactionIndex)
        },
        cells: [
          {
            key: 'date',
            variant: 'TEXT',
            Text: dayjs(transaction?.dateCreated).format('DD MMM YYYY'),
            BottomText: dayjs(transaction?.dateCreated).format('HH:mm:ss'),
          },
          {
            key: 'transactionType',
            variant: DataTableCellVariants.TEXT,
            Text: startCase(transaction?.description).replace('Fdusd', 'FDUSD'),
          },
          {
            key: 'from',
            variant: DataTableCellVariants.TEXT,
            Text: transformedData.fromCurrency && startCase(transformedData.fromCurrency),
            Avatar: transformedData.fromCurrency && (
              <Avatar size="small" name={transformedData.fromCurrency} type="currency" />
            ),
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text: transformedData.toCurrency && startCase(transformedData.toCurrency),
            Avatar: transformedData.toCurrency && (
              <Avatar name={transformedData.toCurrency} type="currency" />
            ),
          },
          {
            key: 'amount',
            variant: isPaymentIn ? DataTableCellVariants.HIGHLIGHT : DataTableCellVariants.TEXT,
            ...(!isPaymentIn && { Text: transformedData.amount }),
            ...(isPaymentIn && {
              Highlights: [
                <Highlight key={transformedData.amount} type="success" variant="status">
                  {`+ ${transformedData.amount}`}
                </Highlight>,
              ],
            }),
            align: DataTableColumnAlignments.RIGHT,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={transaction?.status}
                type={getHighlightType(transaction?.status?.toUpperCase())}
                variant="status"
              >
                {titleCase(transaction?.status)}
              </Highlight>,
              ...(latePayment
                ? [
                    <Highlight key="latePayment" type="neutral" variant="emphasis">
                      {t('latePayment')}
                    </Highlight>,
                  ]
                : []),
            ],
          },
        ],
      }
    })
  }, [walletTransactions?.transactions?.content])

  return (
    <>
      <DataTable name="SimpleTable" headers={columns} data={tableData} />
      <SidePanelSummary
        isOpen={isTransactionDetailsOpen}
        paging={pageTransactions}
        onClose={() => setIsTransactionDetailsOpen(false)}
        transactionDetails={transactionDetails as TransactionContent}
      />
    </>
  )
}

export default memo(SearchTransactionTable)
